import { Skeleton } from '@mui/material'
import { AxiosError } from 'axios'
import Box from 'ext-ui/components/Box/Box'
import Pill from 'ext-ui/components/Pill/Pill'
import { purple500, white100 } from 'ext-ui/style/colors'
import AiStarsIconV2 from 'ext-ui/svg/icon/AiStarsIconV2'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  BackendErrorToastMessage,
  EnableAIExpenseCategoriesToastMessage,
} from 'src/app/Toast/Toast'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  usePatchOrganizationPredictionsSettings,
  usePatchOrganizationSuggestionsSettings,
  useQueryCanUseAIExpenseCategories,
  useQueryOrganizationPredictionsSettings,
  useQueryOrganizationSuggestionsSettings,
} from 'src/queries/organizations/automationSettings/automationSettings.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

import styles from './AutomationRulesSettings.style'

const AutomationRulesSettings: React.FC = (): React.ReactElement => {
  const translate = useTranslate()
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const orgId = useSelector(selectOrganizationIdFromRoute)
  const appName = useSelector(selectAppName)
  const canUseAIExpenseCategories =
    useQueryCanUseAIExpenseCategories()?.data || false
  const setupToastEnabled =
    useSelector(selectFeatureFlaggerFromState)
      ?.enable_ai_expense_categories_setup_toast ?? false

  const showSetupToast = (): void => {
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <EnableAIExpenseCategoriesToastMessage />,
      }),
    )
  }

  // Automation settings hooks
  const { data: suggestionsSettings, isLoading: isLoadingSuggestions } =
    useQueryOrganizationSuggestionsSettings(orgId, {
      enabled: canUseAIExpenseCategories,
    })
  const { data: predictionsSettings, isLoading: isLoadingPredictions } =
    useQueryOrganizationPredictionsSettings(orgId, {
      enabled: canUseAIExpenseCategories,
    })
  const { mutateAsync: patchPredictions } =
    usePatchOrganizationPredictionsSettings()
  const { mutateAsync: patchSuggestions } =
    usePatchOrganizationSuggestionsSettings()

  const handlePredictionsToggle = async (
    enabled: boolean,
  ): Promise<boolean> => {
    if (!orgId) return false

    try {
      await patchPredictions({ orgId, request: { enabled } })

      // Track analytics
      analytics?.track('org settings expense category predictions', {
        screen: calculatedBackgroundLocation.pathname,
        intent: 'toggle expense category predictions',
        interaction: 'button press',
        enabled,
      })

      if (enabled && setupToastEnabled) {
        showSetupToast()
      }

      return true
    } catch (error) {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <BackendErrorToastMessage
              error={error as AxiosError<BackendErrorPayload<string>>}
            />
          ),
          failureColorScheme: true,
          hideImage: true,
        }),
      )
      return false
    }
  }

  const handleSuggestionsToggle = async (
    enabled: boolean,
  ): Promise<boolean> => {
    if (!orgId) return false

    try {
      await patchSuggestions({ orgId, request: { enabled } })

      // Track analytics
      analytics?.track('org settings expense category suggestions', {
        screen: calculatedBackgroundLocation.pathname,
        intent: 'toggle expense category suggestions',
        interaction: 'button press',
        enabled,
      })

      if (enabled && setupToastEnabled) {
        showSetupToast()
      }

      return true
    } catch (error) {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <BackendErrorToastMessage
              error={error as AxiosError<BackendErrorPayload<string>>}
            />
          ),
          failureColorScheme: true,
          hideImage: true,
        }),
      )
      return false
    }
  }

  // if user does not have an active integration they cannot use these features for now
  if (!canUseAIExpenseCategories) return <></>

  return (
    <div className={'AutomationRulesSettings'} css={styles}>
      <Box variant={'highlight'} className={'accounting-rules-settings-box '}>
        <div className={'flex-row gap-3 align-center'}>
          <div className="gradient-icon">
            <AiStarsIconV2 fill={white100} width={24} height={24} />
          </div>
          <div className="header-title">
            {translate('AI-Powered Accounting Rules')}
          </div>
        </div>
        <div className="content-container">
          <Box variant={'highlightContent'} className={'settings-box'}>
            <div className="box-content">
              <div className="text-section">
                <div className="title-row">
                  <div className="title">
                    {translate('Expense Category Smart Suggestions')}
                  </div>
                  <Pill color={'new'}>
                    <span className={'flex-row align-center'}>
                      <AiStarsIconV2 fill={purple500} width={16} height={16} />
                      {translate('AI-Powered')}
                    </span>
                  </Pill>
                </div>
                <div className="description">
                  {translate(
                    'Enable AI-powered recommendations to help you code your transactions in seconds. Simply review and confirm the suggested label for any of your expense categories.',
                  )}
                </div>
              </div>
              <div className="switch-container">
                {isLoadingSuggestions ? (
                  <Skeleton variant="rectangular" width={52} height={32} />
                ) : (
                  <OnOffSwitch
                    defaultOnOffState={suggestionsSettings?.settings?.enabled}
                    onChange={handleSuggestionsToggle}
                    testId="suggestions-toggle"
                  />
                )}
              </div>
            </div>
          </Box>

          <Box variant={'highlightContent'} className={'settings-box'}>
            <div className="box-content">
              <div className="text-section">
                <div className="title-row">
                  <div className="title">
                    {translate('Expense Category Auto-Coding')}
                  </div>
                  <Pill color={'new'}>
                    <span className={'flex-row align-center'}>
                      <AiStarsIconV2 fill={purple500} width={16} height={16} />
                      {translate('AI-Powered')}
                    </span>
                  </Pill>
                </div>
                <div className="description">
                  {translate(
                    'Let ${appName} automatically code your expenses based on your past choices and sync history. Save time by having transactions pre-filled with the right Account. You can always review or adjust.',
                    { appName },
                  )}
                </div>
              </div>
              <div className="switch-container">
                {isLoadingPredictions ? (
                  <Skeleton variant="rectangular" width={52} height={32} />
                ) : (
                  <OnOffSwitch
                    defaultOnOffState={predictionsSettings?.settings?.enabled}
                    onChange={handlePredictionsToggle}
                    testId="predictions-toggle"
                  />
                )}
              </div>
            </div>
          </Box>
        </div>
      </Box>
    </div>
  )
}

export default AutomationRulesSettings
